<template>
  <SoDataTable
    ref="dataTable"
    v-model:filters="filters"
    :headers="headers"
    :items="items"
    :loading="gasData.isFetching"
    :sort-by="[{ key: 'id', order: 'desc' }]"
    filter-width="440px"
  >
    <template #tableActions>
      <div class="d-flex gap-1">
        <SoButton
          v-if="isAddTradeAllowed"
          title="Add Trade"
          icon="plus"
          type="primary"
          large
          data-testid="gas-trade-table-add-btn"
          @click="isAddTradeDialogOpen = true"
        />
        <SoButton
          title="Export"
          icon="download"
          type="primary"
          large
          data-testid="gas-trade-table-export-btn"
          @click="exportGasTrades()"
        />
      </div>
    </template>
    <template #itemActions="rowData">
      <div class="d-flex gap-1 justify-end">
        <SoButton
          v-if="userHasMOPermissions"
          title="confirm"
          type="table-action"
          icon="pencil sm"
          :data-testid="`gas-trade-table-confirm-btn-${rowData.item.id}`"
          @click="handleConfirmClick(rowData.item)"
        />
        <SoButton
          v-if="userHasMOPermissions || rowData.item.canEdit"
          title="edit"
          type="table-action"
          icon="pencil sm"
          :data-testid="`gas-trade-table-update-btn-${rowData.item.id}`"
          @click="handleEditClick(rowData.item)"
        />
        <SoButton
          v-if="userHasMOPermissions && rowData.item.locked"
          title="unlock"
          type="table-action"
          icon="lock-open sm"
          :data-testid="`gas-trade-table-unlock-btn-${rowData.item.id}`"
          @click="handleUnlockClick(rowData.item)"
        />
      </div>
    </template>
    <template #expandableData="rowData">
      <tr v-for="historyItem in rowData.history" :key="historyItem.id">
        <td v-for="column in historyColumns" :key="column">
          {{ parseHistoryData(column, historyItem) }}
        </td>
        <td />
      </tr>
    </template>
    <template #item.id="{ internalItem }">
      <v-badge
        v-if="internalItem.raw.history.length !== 0"
        color="red"
        dot
        style="width: 70%"
      >
        {{ internalItem.raw.id }}
      </v-badge>
    </template>
  </SoDataTable>
  <SoDialog
    v-model="isAddTradeDialogOpen"
    :card-title="isEditForm ? 'Edit Gas Trade' : 'Add Gas Trade'"
    data-testid="gas-trade-table-add-dialog"
    @on-close="resetAddTradeModal()"
  >
    <SoForm
      ref="add-gas-trade-form"
      v-model="isAddTradeValid"
      @submit="addGasTrade()"
    >
      <div class="two-part-dropdown">
        <SoSelect
          v-model="periodFirstField"
          :items="tradePeriodFirstFieldOptionsGas"
          :rules="[rules.required]"
          label="Period"
          type="combobox"
          clearable
        />
        <SoSelect
          v-model="periodSecondField"
          class="no-label-spacer"
          :items="tradePeriodSecondFieldOptions"
          :rules="periodFieldsValidation"
          clearable
        />
      </div>
      <SoSelect
        v-model="indexField"
        :items="tradeIndexes"
        :rules="[rules.required]"
        label="Index"
      />
      <SoTextField
        v-model="priceField"
        :rules="indexValidation"
        type="number"
        label="Price (p/th)"
      />
      <div class="two-part-dropdown">
        <SoSelect
          v-model="buySellField"
          :items="Object.keys(TradeBuySellValues)"
          :rules="[rules.required]"
          label="Volume (th/d)"
        />
        <SoTextField
          v-model="volumeField"
          class="no-label-spacer"
          :rules="[rules.required, rules.positiveNumber]"
          type="number"
          :min="0"
        />
      </div>
      <SoDatePicker
        v-model="invoiceDateRange"
        label="Invoice Date Range"
        type="daterange"
        :error="invoiceDateRangeError"
      />
      <div class="two-part-dropdown">
        <SoSelect
          v-model="counterPartyFirstField"
          :items="tradeCounterpartyFirstFieldOptions"
          :rules="[rules.required]"
          label="CounterParty"
        />
        <SoSelect
          v-model="counterPartySecondField"
          class="no-label-spacer"
          :items="tradeCounterpartySecondFieldOptions"
          :rules="[rules.required]"
        />
      </div>
      <SoTextField
        v-model="traderField"
        :rules="[rules.required]"
        label="Trader"
        readonly
      />
      <SoTextarea v-model="notesField" label="Notes" />
      <v-slide-x-transition>
        <SoAlert
          v-if="gasTradeMutator.isError"
          type="error"
          class="my-4"
          data-testid="gas-trade-add-trade-error"
        >
          {{ gasTradeMutator.error }}
        </SoAlert>
        <SoAlert
          v-else-if="gasTradeMutator.isSuccess"
          type="success"
          class="my-4"
        >
          Trade added.
        </SoAlert>
        <SoAlert
          v-if="updateGasTradeMutator.isError"
          type="error"
          class="my-4"
          data-testid="gas-trade-update-trade-error"
        >
          {{ updateGasTradeMutator.error }}
        </SoAlert>
        <SoAlert
          v-else-if="updateGasTradeMutator.isSuccess"
          type="success"
          class="my-4"
        >
          Trade updated.
        </SoAlert>
      </v-slide-x-transition>
      <div class="d-flex justify-end pt-4 gap-2">
        <SoButton
          title="Cancel"
          large
          data-testid="gas-trade-table-cancel-trade-btn"
          @click="isAddTradeDialogOpen = false"
        />
        <SoButton
          title="Clear"
          large
          data-testid="gas-trade-table-clear-trade-btn"
          @click="clearTradeForm()"
        />
        <SoButton
          title="Save"
          type="primary"
          :disabled="!isAddTradeValid"
          large
          submit
          data-testid="gas-trade-table-save-trade-btn"
        />
      </div>
    </SoForm>
  </SoDialog>
  <ConfirmTradeModal
    v-model="isConfirmTradeDialogOpen"
    data-testid="confirm-gas-trade-modal"
    @submit="($event) => confirmGasTrade($event)"
  >
    <template #alerts>
      <SoAlert
        v-if="confirmGasTradeMutator.isError"
        type="error"
        class="my-4"
        data-testid="gas-trade-confirm-trade-error"
      >
        {{ confirmGasTradeMutator.error }}
      </SoAlert>
      <SoAlert
        v-else-if="confirmGasTradeMutator.isSuccess"
        type="success"
        class="my-4"
      >
        Trade Signed.
      </SoAlert>
    </template>
  </ConfirmTradeModal>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { TradingDataService } from "@/services/operations/TradingDataService"
  import {
    aheadIndexes,
    tradeIndexes,
    tradePeriodFirstFieldOptionsGas,
    tradePeriodSecondFieldOptions,
    tradeCounterpartyFirstFieldOptions,
    tradeCounterpartySecondFieldOptions,
    TradeBuySellValues,
    TradeBuySell,
    gasTradeTableFilters,
  } from "@/constants/tradeOptions"
  import {
    FilterCheckbox,
    FilterType,
    SoDataTableColumnType,
  } from "@/types/components/SoDataTable"
  import { TradesMutator } from "@/mutators/trades/TradesMutator"
  import {
    ITradesConfirmGasData,
    ITradesGasData,
  } from "@/models/operations/tradingData/gasData.types"
  import ConfirmTradeModal from "./ConfirmTradeModal.vue"
  import SoDataTable from "@/components/dataTable/SoDataTable.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoForm from "@/components/formControls/SoForm.vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import SoDatePicker from "@/components/formControls/SoDatePicker.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import rules from "@/validators/rules"
  import dayjs, { Dayjs } from "dayjs"
  import {
    getDateRangeForPeriod,
    getHistoryForGasTrade,
    isCustomPeriod,
    parseHistoryData,
  } from "@/helpers/tradeHelper"
  import { AuthService } from "@/services/AuthService"
  import { ROLES } from "@/services/PermissionsService"
  import SoTextarea from "@/components/formControls/SoTextarea.vue"
  import * as xlsx from "xlsx-republish"

  export default defineComponent({
    components: {
      SoTextarea,
      ConfirmTradeModal,
      SoDataTable,
      SoButton,
      SoDialog,
      SoForm,
      SoSelect,
      SoTextField,
      SoDatePicker,
      SoAlert,
    },
    data() {
      return {
        gasData: TradingDataService.getGasData(),
        gasHistoryData: TradingDataService.getGasHistoryData(),
        gasTradeMutator: TradesMutator.insertGasTrade(),
        gasTradeHistoryMutator: TradesMutator.insertGasTradeHistory(),
        confirmGasTradeMutator: TradesMutator.confirmGasTrade(),
        updateGasTradeMutator: TradesMutator.updateGasTrade(),
        unlockGasTradeMutator: TradesMutator.unlockGasTrade(),
        rules,
        tradeIndexes,
        tradePeriodFirstFieldOptionsGas,
        tradePeriodSecondFieldOptions,
        tradeCounterpartyFirstFieldOptions,
        tradeCounterpartySecondFieldOptions,
        ROLES,
        AuthService,
        TradeBuySellValues,

        isAddTradeDialogOpen: false,
        isAddTradeValid: false,
        isEditForm: false,
        periodFirstField: null as string | null,
        periodSecondField: null as string | null,
        indexField: null as string | null,
        priceField: null as number | null,
        buySellField: null as TradeBuySell | null,
        volumeField: null as number | null,
        counterPartyFirstField: null as string | null,
        counterPartySecondField: null as string | null,
        traderField: AuthService.getCurrentUser().name,
        invoiceDateRange: null as Date[] | null,
        invoiceDateRangeError: "",
        notesField: null as string | null,

        isConfirmTradeDialogOpen: false,
        selectedRowId: null as number | null,
        filters: gasTradeTableFilters,
      }
    },
    computed: {
      headers() {
        return [
          {
            title: "ID",
            key: "id",
            sortable: true,
          },
          {
            title: "Date",
            key: "date",
            fallbackValue: "-",
            sortable: true,
            type: SoDataTableColumnType.DATE,
          },
          {
            title: "Period",
            key: "period",
            fallbackValue: "-",
            sortable: true,
          },
          {
            title: "Index",
            key: "index",
            fallbackValue: "-",
            sortable: true,
          },
          {
            title: "Price (p/th)",
            key: "price",
            fallbackValue: "-",
            sortable: true,
          },
          {
            title: "Volume (th/d)",
            key: "volume",
            fallbackValue: "-",
            sortable: true,
          },
          {
            title: "Total Volume (th)",
            key: "totalVolume",
            fallbackValue: "-",
            sortable: true,
          },
          {
            title: "Date From",
            key: "dateFrom",
            fallbackValue: "-",
            sortable: true,
            type: SoDataTableColumnType.DATE_TIME,
          },
          {
            title: "Date To",
            key: "dateTo",
            fallbackValue: "-",
            sortable: true,
            type: SoDataTableColumnType.DATE_TIME,
          },
          {
            title: "Counterparty",
            key: "counterParty",
            fallbackValue: "-",
            sortable: true,
          },
          {
            title: "Trader",
            key: "trader",
            fallbackValue: "-",
            sortable: true,
          },
          {
            title: "Confirm Received Date",
            key: "confirmReceivedDate",
            fallbackValue: "-",
            sortable: true,
            type: SoDataTableColumnType.DATE,
          },
          {
            title: "Confirm Signed Date",
            key: "confirmSignedDate",
            fallbackValue: "-",
            sortable: true,
            type: SoDataTableColumnType.DATE,
          },
          {
            title: "Confirm Signed By",
            key: "confirmSignedBy",
            fallbackValue: "-",
            sortable: true,
          },
          {
            title: "Notes",
            key: "notes",
            fallbackValue: "-",
            sortable: true,
          },
        ]
      },
      historyColumns() {
        return this.headers.map((header) => header.key)
      },
      items() {
        const items = this.gasData.data ?? []
        return items.map((trade) => ({
          ...trade,
          canEdit:
            trade.trader == AuthService.getCurrentUser().name && !trade.locked,
          history: getHistoryForGasTrade(trade, this.sortedHistoryItems),
        }))
      },
      sortedItems() {
        return [...this.gasData.data].sort((a, b) => (a.id > b.id ? -1 : 1))
      },
      sortedHistoryItems() {
        return [...this.gasHistoryData.data].sort((a, b) =>
          a.id > b.id ? -1 : 1,
        )
      },
      isPriceFieldEmpty() {
        return this.priceField === null
      },
      indexValidation() {
        return this.indexField && aheadIndexes.includes(this.indexField)
          ? []
          : [rules.required]
      },
      periodFields() {
        return [this.periodFirstField, this.periodSecondField]
      },
      periodFieldsValidation() {
        return this.periodFirstField &&
          (isCustomPeriod(this.periodFirstField) ||
            !tradePeriodFirstFieldOptionsGas.includes(this.periodFirstField))
          ? []
          : [rules.required]
      },
      isAddTradeAllowed() {
        return (
          AuthService.isDeveloper() ||
          AuthService.getUserRoles().includes(ROLES.TRADING_FO)
        )
      },
      userHasMOPermissions() {
        return (
          AuthService.isDeveloper() ||
          AuthService.getUserRoles().includes(ROLES.TRADING_MO)
        )
      },
    },
    watch: {
      items(val) {
        val.forEach((trade: ITradesGasData) => {
          const computedFilters = [
            "trader",
            "confirmSignedBy",
            "period",
            "counterParty",
          ]

          computedFilters.forEach((column) => {
            if (
              this.filters[column].type === FilterType.CHECKBOX &&
              trade[column] !== null
            ) {
              ;(this.filters[column] as FilterCheckbox).options[trade[column]] =
                trade[column]
            }
          })
        })
      },
      isAddTradeDialogOpen(val) {
        if (!val) {
          if (this.gasTradeMutator.isSuccess || this.gasTradeMutator.isError) {
            this.gasTradeMutator.reset()
          } else if (
            this.updateGasTradeMutator.isSuccess ||
            this.updateGasTradeMutator.isError
          ) {
            this.updateGasTradeMutator.reset()
          }
        }
      },
      isConfirmTradeDialogOpen(val) {
        if (!val) {
          if (
            this.confirmGasTradeMutator.isSuccess ||
            this.confirmGasTradeMutator.isError
          ) {
            this.confirmGasTradeMutator.reset()
          }
        }
      },
      periodFields(val) {
        const [period, year] = val

        if (period && isCustomPeriod(period)) {
          this.periodSecondField = null
          this.invoiceDateRange = getDateRangeForPeriod(period, null)
        } else if (period && year) {
          this.invoiceDateRange = getDateRangeForPeriod(period, year)
        } else this.invoiceDateRange = null
      },
    },
    methods: {
      parseHistoryData,
      async addGasTrade() {
        if (!this.invoiceDateRange) {
          this.invoiceDateRangeError = "Date is required"
          return
        }
        this.invoiceDateRangeError = ""

        const tradeData = {
          counterParty: `${this.counterPartyFirstField}-${this.counterPartySecondField}`,
          date: null,
          dateFrom: dayjs(this.invoiceDateRange[0]),
          dateTo: dayjs(this.invoiceDateRange[1]),
          index: this.indexField,
          period: this.getPeriod(),
          price: this.isPriceFieldEmpty ? null : Number(this.priceField),
          trader: this.traderField,
          volume:
            TradeBuySellValues[this.buySellField || "Buy"].multiplier *
            Number(this.volumeField),
          id: this.isEditForm
            ? Number(this.selectedRowId)
            : Number(this.sortedItems[0].id) + 1,
          notes: this.notesField,
        }

        if (this.isEditForm) {
          const tradeToBeEdited = this.gasData.data.find(
            (trade) => trade.id === this.selectedRowId,
          )

          if (tradeToBeEdited) {
            await this.gasTradeHistoryMutator.mutateAsync({
              ...tradeToBeEdited,
              gasTradeId: tradeToBeEdited.id,
              id: this.sortedHistoryItems[0]
                ? this.sortedHistoryItems[0].id + 1
                : 1,
            })
            await this.updateGasTradeMutator.mutateAsync(tradeData)
            await this.gasData.refetch()
          }
        } else {
          this.gasTradeMutator.mutateAsync(tradeData).then(() => {
            this.gasData.refetch()
          })
        }
      },
      handleConfirmClick(row) {
        this.selectedRowId = row.id
        this.isConfirmTradeDialogOpen = true
      },
      handleEditClick(row: ITradesGasData) {
        this.selectedRowId = row.id
        this.isEditForm = true
        this.isAddTradeDialogOpen = true

        const [firstField, secondField] = this.getCounterpartyFields(
          row.counterParty as string,
        )
        this.counterPartyFirstField = firstField
        this.counterPartySecondField = secondField
        this.indexField = row.index
        this.priceField = row.price
        this.buySellField = (row.volume as number) < 0 ? "Sell" : "Buy"
        this.volumeField = Math.abs(row.volume as number)
        this.traderField = row.trader as string
        this.notesField = row.notes

        const customPeriod = isCustomPeriod(row.period as string)
        this.periodFirstField = this.getFirstFieldForPeriod(
          row.period as string,
          customPeriod,
        )
        this.periodSecondField = this.getSecondFieldForPeriod(
          row.period as string,
          customPeriod,
        )
      },
      handleUnlockClick(row: ITradesGasData) {
        this.unlockGasTradeMutator.mutateAsync({ id: row.id }).then(() => {
          this.gasData.refetch()
        })
      },
      confirmGasTrade(args: ITradesConfirmGasData) {
        if (!this.selectedRowId) return

        this.confirmGasTradeMutator
          .mutateAsync({
            id: this.selectedRowId,
            confirmReceivedDate: args.confirmReceivedDate,
            confirmSignedBy: args.confirmSignedBy,
            confirmSignedDate: args.confirmSignedDate,
          })
          .then(() => {
            this.gasData.refetch()
          })
      },
      clearTradeForm() {
        const form = this.$refs["add-gas-trade-form"] as typeof SoForm
        form?.reset()
        this.traderField = AuthService.getCurrentUser().name
      },
      resetAddTradeModal() {
        this.clearTradeForm()
        this.isEditForm = false
        this.selectedRowId = null
      },
      getPeriod() {
        return this.periodSecondField
          ? `${this.periodFirstField}${(
              this.periodSecondField as string
            ).substring(2)}`
          : this.periodFirstField
      },
      getFirstFieldForPeriod(period: string, customPeriod: boolean) {
        return customPeriod ? period : period.slice(0, -2)
      },
      getSecondFieldForPeriod(period: string, customPeriod: boolean) {
        return customPeriod
          ? null
          : (tradePeriodSecondFieldOptions.find(
              (year) => year.title == period.slice(-2),
            )?.value as string)
      },
      getCounterpartyFields(counterParty: string) {
        return counterParty.split("-")
      },
      exportGasTrades() {
        const dataTable = this.$refs.dataTable as InstanceType<
          typeof SoDataTable
        >

        const worksheet = xlsx.utils.json_to_sheet(
          dataTable.dataTableItems.map((item) => {
            const { ...columnsToExport } = item
            return {
              ...columnsToExport,
              date: (item.date as Dayjs).toDate(),
              dateFrom: (item.dateFrom as Dayjs).toDate(),
              dateTo: (item.dateTo as Dayjs).toDate(),
              confirmReceivedDate: item.confirmReceivedDate
                ? (item.confirmReceivedDate as Dayjs).toDate()
                : "",
              confirmSignedDate: item.confirmSignedDate
                ? (item.confirmSignedDate as Dayjs).toDate()
                : "",
            }
          }),
          {
            header: this.headers.map((header) => header.key),
            dateNF: "d mmm yy",
          },
        )
        xlsx.utils.sheet_add_aoa(worksheet, [
          this.headers.map((header) => header.title),
        ])

        const workbook = xlsx.utils.book_new()
        xlsx.utils.book_append_sheet(workbook, worksheet, "Sheet1")
        xlsx.writeFile(workbook, `gas_trades_${dayjs().format("DDMMYY")}.xlsx`)
      },
    },
  })
</script>
<style scoped>
  .two-part-dropdown {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 1rem;
  }

  .no-label-spacer {
    margin-top: 27px;
  }
  .so-data-table .v-data-table .v-table__wrapper > table tbody > tr > td {
    background-color: rgb(var(--v-theme-surface-dim));
  }
</style>
